/**
 * Aquaplanet colour gallery + colour swatches + size buttons.
 * Requires the product tag "colour-gallery" and the variant JSON block
 * data-colour-gallery-variant-data in Product Form.
 *
 * Image ordering:
 * Within the selected colour's images, an explicit number in the ALT text
 * (e.g. "Colour:navy Back 1" or just "1") pins that image to that position
 * (1 = first, 2 = second, ...). Numbered images always come before
 * unnumbered ones; unnumbered images keep the automatic front/back/detail
 * priority. Numbers only apply within the matched colour, so a "1" on a
 * navy image has no effect when white is selected.
 */
(function () {
  'use strict';

  const DESKTOP_QUERY = '(min-width: 750px)';
  const ORIGINAL_STATE = new WeakMap();

  const COLOURS = {
    white: '#ffffff',
    navy: '#222c3e',
    grey: '#aeaeae',
    'heather-grey': '#aeaeae',
    stone: '#a6a18a',
    'natural-raw': '#eee7da',
    natural: '#eee7da',
    black: '#1f1f1f',
    blue: '#3a5f8a',
    green: '#58704c',
    khaki: '#7d765f',
    sand: '#d8cfbf'
  };

  let refreshTimer = null;
  let pendingReset = false;
  let resizeTimer = null;
  let galleryUpdateToken = 0;

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function normalise(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\bgray\b/g, 'grey')
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function aliases(value) {
    const key = normalise(value);

    const map = {
      grey: ['grey', 'heather-grey'],
      'heather-grey': ['grey', 'heather-grey'],
      natural: ['natural', 'natural-raw'],
      'natural-raw': ['natural', 'natural-raw']
    };

    return map[key] || [key];
  }

  function colourMatches(value, possibleValues) {
    const first = aliases(value);
    const values = Array.isArray(possibleValues)
      ? possibleValues
      : [possibleValues];

    return values.some(function (possible) {
      const second = aliases(possible);

      return first.some(function (key) {
        return second.includes(key);
      });
    });
  }

  function cssEscape(value) {
    if (
      window.CSS &&
      typeof window.CSS.escape === 'function'
    ) {
      return window.CSS.escape(value);
    }

    return String(value).replace(/["\\]/g, '\\$&');
  }

  function rootElement() {
    return (
      document.getElementById('shopify-section-product') ||
      document.querySelector('.product-details-upper') ||
      document
    );
  }

  function formRoot(root) {
    return (
      root.querySelector('.product-form-snippet') ||
      root
    );
  }

  function optionSelects(root) {
    return Array.from(
      formRoot(root).querySelectorAll(
        'select[data-option-select]'
      )
    );
  }

  function labelFor(select, root) {
    if (!select || !select.id) {
      return null;
    }

    return formRoot(root).querySelector(
      'label[for="' +
        cssEscape(select.id) +
        '"]'
    );
  }

  function fieldText(select, root) {
    const label = labelFor(select, root);

    return [
      label ? label.textContent : '',
      select ? select.name : '',
      select ? select.id : '',
      select
        ? select.getAttribute('aria-label')
        : ''
    ].join(' ');
  }

  function findField(root, type) {
    const selects = optionSelects(root);
    const matcher =
      type === 'colour'
        ? /colou?r/i
        : /\bsize\b/i;

    let select = selects.find(function (item) {
      return matcher.test(
        fieldText(item, root)
      );
    });

    if (!select && type === 'colour') {
      select = selects.find(function (item) {
        return Array.from(
          item.options
        ).some(function (option) {
          return Boolean(
            COLOURS[
              normalise(option.value)
            ]
          );
        });
      });
    }

    if (!select && type === 'colour') {
  // Fallback for older Size + Colour products
  select = formRoot(root).querySelector('#option-2');

  // If there is only one option, Colour will usually be option 1
  if (!select) {
    select = formRoot(root).querySelector('#option-1');
  }
}

    if (!select) {
      return null;
    }

    return {
      select: select,
      label: labelFor(select, root),
      wrapper:
        select.closest('.select-wrapper'),
      formRoot: formRoot(root)
    };
  }

  function colourField(root) {
    return findField(root, 'colour');
  }

  function sizeField(root) {
    return findField(root, 'size');
  }

  function hideNativeField(field) {
    if (!field) {
      return;
    }

    if (field.label) {
      field.label.classList.add(
        'colour-gallery-native-option-hidden'
      );
    }

    if (field.wrapper) {
      field.wrapper.classList.add(
        'colour-gallery-native-option-hidden'
      );
    } else {
      field.select.classList.add(
        'colour-gallery-native-option-hidden'
      );
    }
  }

  function clonedTitle(field, fallback) {
    let title;

    if (field && field.label) {
      title =
        field.label.cloneNode(true);

      title.removeAttribute('for');

      title.classList.remove(
        'colour-gallery-native-option-hidden'
      );
    } else {
      title =
        document.createElement('label');

      title.className = 'label';
      title.textContent = fallback;
    }

    return title;
  }

  function changeNativeSelect(
    select,
    value
  ) {
    if (
      !select ||
      !value ||
      select.value === value
    ) {
      return;
    }

    select.value = value;

    select.dispatchEvent(
      new Event('input', {
        bubbles: true
      })
    );

    select.dispatchEvent(
      new Event('change', {
        bubbles: true
      })
    );
  }

  function variantData(root) {
    const node = root.querySelector(
      '[data-colour-gallery-variant-data]'
    );

    if (!node) {
      return null;
    }

    if (node.__colourGalleryData) {
      return node.__colourGalleryData;
    }

    try {
      const data = JSON.parse(
        node.textContent || '{}'
      );

      data.options = Array.isArray(
        data.options
      )
        ? data.options
        : [];

      data.variants = Array.isArray(
        data.variants
      )
        ? data.variants
        : [];

      node.__colourGalleryData = data;

      return data;
    } catch (error) {
      console.warn(
        '[Colour gallery] Invalid variant JSON.',
        error
      );

      return null;
    }
  }

  function optionIndex(select, root) {
    const match = String(
      select.id || ''
    ).match(/^option-(\d+)$/i);

    if (match) {
      return (
        parseInt(match[1], 10) - 1
      );
    }

    return optionSelects(root).indexOf(
      select
    );
  }

  function sizeAvailable(
    root,
    select,
    sizeValue
  ) {
    const data = variantData(root);

    if (
      !data ||
      !data.variants.length
    ) {
      return true;
    }

    const values = optionSelects(
      root
    ).map(function (item) {
      return item.value;
    });

    const index = optionIndex(
      select,
      root
    );

    if (index < 0) {
      return true;
    }

    values[index] = sizeValue;

    return data.variants.some(
      function (variant) {
        if (
          !variant ||
          !variant.available ||
          !Array.isArray(
            variant.options
          )
        ) {
          return false;
        }

        return values.every(
          function (
            value,
            optionPosition
          ) {
            return (
              normalise(
                variant.options[
                  optionPosition
                ]
              ) ===
              normalise(value)
            );
          }
        );
      }
    );
  }

  function placeBefore(
    element,
    reference
  ) {
    if (
      !element ||
      !reference ||
      !reference.parentNode
    ) {
      return;
    }

    if (
      element.parentNode !==
        reference.parentNode ||
      element.nextElementSibling !==
        reference
    ) {
      reference.parentNode.insertBefore(
        element,
        reference
      );
    }
  }

  function positionSize(
    container,
    field
  ) {
    if (!container || !field) {
      return;
    }

    placeBefore(
      container,
      field.label ||
        field.wrapper ||
        field.select
    );
  }

  function positionColour(
    container,
    field,
    root
  ) {
    if (!container || !field) {
      return;
    }

    const customSize =
      field.formRoot.querySelector(
        '[data-colour-gallery-size-options]'
      );

    if (customSize) {
      placeBefore(
        container,
        customSize
      );

      return;
    }

    const size = sizeField(root);

    if (size) {
      placeBefore(
        container,
        size.label ||
          size.wrapper ||
          size.select
      );

      return;
    }

    placeBefore(
      container,
      field.label ||
        field.wrapper ||
        field.select
    );
  }

  function swatchHex(value) {
    const key = normalise(value);

    if (COLOURS[key]) {
      return COLOURS[key];
    }

    const equivalent = aliases(key);

    for (
      let index = 0;
      index < equivalent.length;
      index += 1
    ) {
      if (
        COLOURS[
          equivalent[index]
        ]
      ) {
        return COLOURS[
          equivalent[index]
        ];
      }
    }

    return '#d8dce2';
  }

  function syncColourButtons(root) {
    const field = colourField(root);

    if (!field) {
      return;
    }

    const container =
      field.formRoot.querySelector(
        '[data-colour-gallery-swatches]'
      );

    if (!container) {
      return;
    }

    container
      .querySelectorAll(
        '[data-colour-gallery-swatch-value]'
      )
      .forEach(function (button) {
        const value =
          button.getAttribute(
            'data-colour-gallery-swatch-value'
          ) || '';

        const active =
          colourMatches(
            field.select.value,
            value
          );

        button.classList.toggle(
          'is-active',
          active
        );

        button.setAttribute(
          'aria-checked',
          active
            ? 'true'
            : 'false'
        );
      });
  }

  function syncSizeButtons(root) {
    const field = sizeField(root);

    if (!field) {
      return;
    }

    const container =
      field.formRoot.querySelector(
        '[data-colour-gallery-size-options]'
      );

    if (!container) {
      return;
    }

    container
      .querySelectorAll(
        '[data-colour-gallery-size-value]'
      )
      .forEach(function (button) {
        const value =
          button.getAttribute(
            'data-colour-gallery-size-value'
          ) || '';

        const active =
          normalise(value) ===
          normalise(
            field.select.value
          );

        const available =
          sizeAvailable(
            root,
            field.select,
            value
          );

        button.classList.toggle(
          'is-active',
          active
        );

        button.classList.toggle(
          'is-unavailable',
          !available
        );

        button.disabled = !available;

        button.setAttribute(
          'aria-pressed',
          active
            ? 'true'
            : 'false'
        );

        button.setAttribute(
          'aria-disabled',
          available
            ? 'false'
            : 'true'
        );
      });
  }

  function syncControls(root) {
    syncColourButtons(root);
    syncSizeButtons(root);
  }

  function buildColourButtons(root) {
    const field = colourField(root);

    if (!field) {
      return;
    }

    let container =
      field.formRoot.querySelector(
        '[data-colour-gallery-swatches]'
      );

    if (
      container &&
      container.__nativeSelect !==
        field.select
    ) {
      container.remove();
      container = null;
    }

    if (!container) {
      container =
        document.createElement('div');

      container.className =
        'colour-gallery-variant-swatches';

      container.setAttribute(
        'data-colour-gallery-swatches',
        ''
      );

      container.__nativeSelect =
        field.select;

      const title =
        clonedTitle(
          field,
          'Select Colour:'
        );

      title.classList.add(
        'colour-gallery-variant-swatches__title'
      );

      const list =
        document.createElement('div');

      list.className =
        'colour-gallery-variant-swatches__list';

      list.setAttribute(
        'role',
        'radiogroup'
      );

      list.setAttribute(
        'aria-label',
        String(
          title.textContent ||
            'Select Colour'
        ).trim()
      );

      Array.from(
        field.select.options
      ).forEach(function (option) {
        if (
          !String(
            option.value || ''
          ).trim()
        ) {
          return;
        }

        const key =
          normalise(option.value);

        const button =
          document.createElement(
            'button'
          );

        const chip =
          document.createElement(
            'span'
          );

        const text =
          document.createElement(
            'span'
          );

        button.type = 'button';

        button.className =
          'colour-gallery-variant-swatches__button';

        button.setAttribute(
          'role',
          'radio'
        );

        button.setAttribute(
          'aria-checked',
          'false'
        );

        button.setAttribute(
          'aria-label',
          'Select ' +
            option.textContent.trim()
        );

        button.setAttribute(
          'data-colour-gallery-swatch-value',
          option.value
        );

        chip.className =
          'colour-gallery-variant-swatches__chip';

        chip.style.backgroundColor =
          swatchHex(option.value);

        if (
          key === 'white' ||
          key === 'natural' ||
          key === 'natural-raw'
        ) {
          chip.style.borderColor =
            '#c7d0d9';
        }

        text.className =
          'colour-gallery-variant-swatches__text';

        text.textContent =
          option.textContent.trim();

        button.appendChild(chip);
        button.appendChild(text);
        list.appendChild(button);
      });

      list.addEventListener(
        'click',
        function (event) {
          const button =
            event.target.closest(
              '[data-colour-gallery-swatch-value]'
            );

          if (!button) {
            return;
          }

          changeNativeSelect(
            field.select,
            button.getAttribute(
              'data-colour-gallery-swatch-value'
            )
          );
        }
      );

      container.appendChild(title);
      container.appendChild(list);
    }

    positionColour(
      container,
      field,
      root
    );

    hideNativeField(field);
    syncColourButtons(root);
  }

  function buildSizeButtons(root) {
    const field = sizeField(root);

    if (!field) {
      return;
    }

    let container =
      field.formRoot.querySelector(
        '[data-colour-gallery-size-options]'
      );

    if (
      container &&
      container.__nativeSelect !==
        field.select
    ) {
      container.remove();
      container = null;
    }

    if (!container) {
      container =
        document.createElement('div');

      container.className =
        'colour-gallery-size-options';

      container.setAttribute(
        'data-colour-gallery-size-options',
        ''
      );

      container.__nativeSelect =
        field.select;

      const title =
        clonedTitle(
          field,
          'Size'
        );

      title.classList.add(
        'colour-gallery-size-options__title'
      );

      title.textContent =
        String(
          title.textContent || 'Size'
        )
          .replace(
            /^\s*select\s+/i,
            ''
          )
          .replace(/:\s*$/, '')
          .trim() || 'Size';

      const list =
        document.createElement('div');

      list.className =
        'colour-gallery-size-options__list';

      list.setAttribute(
        'role',
        'group'
      );

      list.setAttribute(
        'aria-label',
        title.textContent
      );

      Array.from(
        field.select.options
      ).forEach(function (option) {
        if (
          !String(
            option.value || ''
          ).trim()
        ) {
          return;
        }

        const button =
          document.createElement(
            'button'
          );

        button.type = 'button';

        button.className =
          'colour-gallery-size-options__button';

        button.textContent =
          option.textContent.trim();

        button.setAttribute(
          'aria-label',
          'Select size ' +
            option.textContent.trim()
        );

        button.setAttribute(
          'data-colour-gallery-size-value',
          option.value
        );

        list.appendChild(button);
      });

      list.addEventListener(
        'click',
        function (event) {
          const button =
            event.target.closest(
              '[data-colour-gallery-size-value]'
            );

          if (
            !button ||
            button.disabled
          ) {
            return;
          }

          changeNativeSelect(
            field.select,
            button.getAttribute(
              'data-colour-gallery-size-value'
            )
          );
        }
      );

      container.appendChild(title);
      container.appendChild(list);
    }

    positionSize(
      container,
      field
    );

    hideNativeField(field);
    syncSizeButtons(root);
  }

  function buildControls(root) {
    buildSizeButtons(root);
    buildColourButtons(root);
    syncControls(root);
  }

  function mainGallery(root) {
    return (
      root.querySelector(
        '#product-media-slider'
      ) ||
      root.querySelector(
        '.product-media-list-mobile'
      ) ||
      root.querySelector(
        '.product-media-list'
      )
    );
  }

  function thumbGallery(root) {
    return (
      root.querySelector(
        '#product-thumbs-slider'
      ) ||
      root.querySelector(
        '[data-id="product-thumbs-slider"]'
      ) ||
      root.querySelector(
        '[data-navigation-for="product-media-slider"]'
      )
    );
  }

  function slidesContainer(gallery) {
    if (!gallery) {
      return null;
    }

    return (
      gallery.querySelector(
        '.glide__slides'
      ) ||
      gallery.querySelector('ul')
    );
  }

  function slides(
    container,
    includeClones
  ) {
    if (!container) {
      return [];
    }

    return Array.from(
      container.children
    ).filter(function (child) {
      const valid =
        child.matches &&
        child.matches(
          '.glide__slide, .product-image-wrap, [data-image-index], [data-media-id]'
        );

      if (!valid) {
        return false;
      }

      return (
        includeClones ||
        !child.classList.contains(
          'glide__slide--clone'
        )
      );
    });
  }

  function mediaContainer(slide) {
    if (!slide) {
      return null;
    }

    return (
      slide.querySelector(
        '.product-media'
      ) ||
      slide.querySelector(
        '.product-image-wrap'
      ) ||
      slide
    );
  }

  function mediaText(element) {
    if (!element) {
      return '';
    }

    const parts = [];

    element
      .querySelectorAll(
        'img, source'
      )
      .forEach(function (node) {
        [
          'alt',
          'data-alt',
          'src',
          'srcset',
          'data-src',
          'data-srcset'
        ].forEach(function (name) {
          const value =
            node.getAttribute(name);

          if (value) {
            parts.push(value);
          }
        });
      });

    return parts.join(' | ');
  }

  /**
   * Alt text only (no src/srcset), used for explicit numeric ordering.
   * Filenames/URLs are full of incidental numbers (dimensions, IDs),
   * so ordering only ever reads the alt attributes.
   */
  function mediaAltText(element) {
    if (!element) {
      return '';
    }

    const parts = [];

    element
      .querySelectorAll(
        'img, source'
      )
      .forEach(function (node) {
        ['alt', 'data-alt'].forEach(
          function (name) {
            const value =
              node.getAttribute(name);

            if (value) {
              parts.push(value);
            }
          }
        );
      });

    return parts.join(' | ');
  }

  /**
   * Extracts an explicit order number from alt text.
   * Works for "Colour:navy Back 1", "navy 2", or just "1".
   * Returns null when no standalone number is present.
   * The last standalone number wins, so any colour label or
   * descriptive text can sit in front of it.
   */
  function orderNumber(altText) {
    const source = String(
      altText || ''
    );

    if (!source.trim()) {
      return null;
    }

    // Strip "Colour:xxx" style prefixes so a colour value
    // never gets misread as an order number.
    const cleaned = source.replace(
      /colou?r\s*:\s*[a-z _-]+/gi,
      ' '
    );

    const pattern =
      /(?:^|[\s_\-.|,;:()[\]{}])(\d{1,3})(?=$|[\s_\-.|,;:()[\]{}])/g;

    let match;
    let found = null;

    while (
      (match = pattern.exec(cleaned)) !==
      null
    ) {
      found = parseInt(
        match[1],
        10
      );
    }

    return found;
  }

  function mediaRatio(element) {
    const image = element
      ? element.querySelector('img')
      : null;

    if (!image) {
      return null;
    }

    const width =
      parseFloat(
        image.getAttribute('width')
      ) ||
      parseFloat(
        image.getAttribute(
          'data-width'
        )
      ) ||
      image.naturalWidth ||
      0;

    const height =
      parseFloat(
        image.getAttribute('height')
      ) ||
      parseFloat(
        image.getAttribute(
          'data-height'
        )
      ) ||
      image.naturalHeight ||
      0;

    return width && height
      ? width / height
      : null;
  }

  function addKey(keys, value) {
    const key = normalise(value);

    if (
      key &&
      !keys.includes(key)
    ) {
      keys.push(key);
    }
  }

  function mediaColourKeys(text) {
    const keys = [];
    const source = String(text || '');

    const explicit =
      /(?:^|[\s|,;()[\]{}])colou?r\s*:\s*([a-z0-9 _-]+)/gi;

    let match;

    while (
      (match = explicit.exec(source)) !==
      null
    ) {
      addKey(
        keys,
        match[1]
          .split('|')[0]
          .split(',')[0]
          .split(';')[0]
          .trim()
      );
    }

    const lower =
      ' ' +
      source
        .toLowerCase()
        .replace(/%20/g, ' ')
        .replace(/\+/g, ' ') +
      ' ';

    [
      [
        'grey',
        [
          'heather_grey',
          'heather-grey',
          'heather grey',
          '_grey_',
          '-grey-',
          ' grey ',
          '_gray_',
          '-gray-',
          ' gray '
        ]
      ],
      [
        'navy',
        [
          '_navy_',
          '-navy-',
          ' navy '
        ]
      ],
      [
        'white',
        [
          '_white_',
          '-white-',
          ' white '
        ]
      ],
      [
        'stone',
        [
          '_stone_',
          '-stone-',
          ' stone '
        ]
      ],
      [
        'natural-raw',
        [
          'natural_raw',
          'natural-raw',
          'natural raw'
        ]
      ],
      [
        'black',
        [
          '_black_',
          '-black-',
          ' black '
        ]
      ],
      [
        'blue',
        [
          '_blue_',
          '-blue-',
          ' blue '
        ]
      ],
      [
        'green',
        [
          '_green_',
          '-green-',
          ' green '
        ]
      ],
      [
        'khaki',
        [
          '_khaki_',
          '-khaki-',
          ' khaki '
        ]
      ],
      [
        'sand',
        [
          '_sand_',
          '-sand-',
          ' sand '
        ]
      ]
    ].forEach(function (rule) {
      if (
        rule[1].some(function (test) {
          return lower.includes(test);
        })
      ) {
        addKey(keys, rule[0]);
      }
    });

    return keys;
  }

  function priority(item) {
    const text = String(
      item.text || ''
    ).toLowerCase();

    let score = 0;

    if (
      /\bfront\b/.test(text) ||
      text.includes('_front_') ||
      text.includes('-front-')
    ) {
      score += 100;
    }

    if (
      item.ratio &&
      item.ratio < 0.9
    ) {
      score += 40;
    }

    if (
      /\bback\b/.test(text) ||
      text.includes('_back_') ||
      text.includes('-back-')
    ) {
      score += 10;
    }

    if (
      item.ratio &&
      item.ratio >= 0.95 &&
      item.ratio <= 1.05
    ) {
      score -= 25;
    }

    if (
      /detail|close-up|closeup|zoom|crop/.test(
        text
      )
    ) {
      score -= 50;
    }

    return score;
  }

  function cacheGallery(container) {
    if (!container) {
      return [];
    }

    const realSlides =
      slides(container, false);

    const cached =
      ORIGINAL_STATE.get(container);

    const refresh =
      !cached ||
      cached.length !==
        realSlides.length ||
      cached.some(function (item) {
        return !container.contains(
          item.slide
        );
      });

    if (!refresh) {
      return cached;
    }

    const state = realSlides.map(
      function (slide, index) {
        const media =
          mediaContainer(slide);

        const alt =
          mediaAltText(slide);

        return {
          slide: slide,
          index: index,

          key:
            slide.getAttribute(
              'data-media-id'
            ) ||
            slide.getAttribute(
              'data-image-index'
            ) ||
            String(index),

          html: media
            ? media.innerHTML
            : slide.innerHTML,

          text: mediaText(slide),

          alt: alt,

          order:
            orderNumber(alt),

          ratio:
            mediaRatio(slide),

          hidden:
            slide.classList.contains(
              'hidden-group'
            ),

          hiddenValue:
            slide.getAttribute(
              'data-hidden-group'
            )
        };
      }
    );

    ORIGINAL_STATE.set(
      container,
      state
    );

    return state;
  }

  function orderedMedia(
    container,
    selectedColour
  ) {
    const original =
      cacheGallery(container);

    const matching =
      original.filter(function (item) {
        return colourMatches(
          selectedColour,
          mediaColourKeys(item.text)
        );
      });

    if (!matching.length) {
      return null;
    }

    matching.sort(
      function (first, second) {
        const firstNumbered =
          first.order !== null &&
          first.order !== undefined;

        const secondNumbered =
          second.order !== null &&
          second.order !== undefined;

        // Explicit alt-text numbers always win,
        // sorted ascending (1, 2, 3, ...).
        if (
          firstNumbered &&
          secondNumbered
        ) {
          return (
            first.order -
              second.order ||
            first.index -
              second.index
          );
        }

        if (firstNumbered) {
          return -1;
        }

        if (secondNumbered) {
          return 1;
        }

        // No numbers: fall back to the
        // automatic front/back/detail priority.
        return (
          priority(second) -
            priority(first) ||
          first.index -
            second.index
        );
      }
    );

    return matching.concat(
      original.filter(function (item) {
        return !matching.includes(item);
      })
    );
  }

  function bestImageUrl(html) {
    if (!html) {
      return '';
    }

    const template =
      document.createElement('template');

    template.innerHTML = html;

    const image =
      template.content.querySelector(
        'img'
      );

    if (!image) {
      return '';
    }

    const srcset =
      image.getAttribute(
        'data-srcset'
      ) ||
      image.getAttribute(
        'srcset'
      ) ||
      '';

    if (srcset) {
      const candidates = srcset
        .split(',')
        .map(function (candidate) {
          const pieces =
            candidate
              .trim()
              .split(/\s+/);

          return {
            url: pieces[0] || '',
            width:
              parseInt(
                pieces[1],
                10
              ) || 0
          };
        })
        .filter(function (candidate) {
          return candidate.url;
        });

      candidates.sort(
        function (first, second) {
          return (
            first.width -
            second.width
          );
        }
      );

      if (candidates.length) {
        return candidates[
          candidates.length - 1
        ].url;
      }
    }

    return (
      image.getAttribute(
        'data-src'
      ) ||
      image.getAttribute('src') ||
      ''
    );
  }

  function preloadHtml(html) {
    const url =
      bestImageUrl(html);

    if (
      !url ||
      url.indexOf('data:image') === 0
    ) {
      return Promise.resolve();
    }

    return new Promise(
      function (resolve) {
        const image = new Image();
        let settled = false;

        const finish = function () {
          if (settled) {
            return;
          }

          settled = true;
          resolve();
        };

        image.onload = finish;
        image.onerror = finish;
        image.src = url;

        if (image.complete) {
          finish();
        }

        window.setTimeout(
          finish,
          1200
        );
      }
    );
  }

  function applySource(
    slide,
    source
  ) {
    if (
      !slide ||
      !source ||
      !source.html
    ) {
      return;
    }

    if (
      slide.getAttribute(
        'data-colour-gallery-source'
      ) === source.key
    ) {
      return;
    }

    const media =
      mediaContainer(slide);

    if (!media) {
      return;
    }

    media.innerHTML =
      source.html;

    slide.setAttribute(
      'data-colour-gallery-source',
      source.key
    );
  }

  async function swapGallery(
    container,
    selectedColour,
    token
  ) {
    if (!container) {
      return false;
    }

    const order =
      orderedMedia(
        container,
        selectedColour
      );

    const realSlides =
      slides(container, false);

    if (
      !order ||
      !order.length
    ) {
      return false;
    }

    const sources =
      realSlides.map(
        function (_, index) {
          return (
            order[index] ||
            order[
              index % order.length
            ]
          );
        }
      );

    await Promise.all(
      sources.map(function (source) {
        return preloadHtml(
          source
            ? source.html
            : ''
        );
      })
    );

    if (
      token !== galleryUpdateToken
    ) {
      return false;
    }

    requestAnimationFrame(
      function () {
        realSlides.forEach(
          function (slide, index) {
            applySource(
              slide,
              sources[index]
            );
          }
        );

        const clones =
          slides(
            container,
            true
          ).filter(function (slide) {
            return slide.classList.contains(
              'glide__slide--clone'
            );
          });

        clones.forEach(
          function (clone, index) {
            const source =
              sources[
                index %
                  sources.length
              ];

            applySource(
              clone,
              source
            );
          }
        );
      }
    );

    return true;
  }

  function restoreMobileClasses(
    container
  ) {
    cacheGallery(
      container
    ).forEach(function (item) {
      const slide = item.slide;

      slide.classList.remove(
        'colour-gallery-square-slot',
        'colour-gallery-visible-slot',
        'colour-gallery-hidden-slot'
      );

      slide.removeAttribute(
        'data-colour-gallery-position'
      );

      if (item.hidden) {
        slide.classList.add(
          'hidden-group'
        );
      } else {
        slide.classList.remove(
          'hidden-group'
        );
      }

      if (
        item.hiddenValue === null
      ) {
        slide.removeAttribute(
          'data-hidden-group'
        );
      } else {
        slide.setAttribute(
          'data-hidden-group',
          item.hiddenValue
        );
      }
    });
  }

  function desktopLayout(container) {
    slides(
      container,
      false
    ).forEach(function (slide, index) {
      const position = index + 1;

      slide.setAttribute(
        'data-colour-gallery-position',
        String(position)
      );

      slide.classList.remove(
        'colour-gallery-square-slot',
        'colour-gallery-visible-slot',
        'colour-gallery-hidden-slot'
      );

      if (index < 6) {
        slide.classList.remove(
          'hidden-group'
        );

        slide.removeAttribute(
          'data-hidden-group'
        );

        slide.classList.add(
          'colour-gallery-visible-slot'
        );
      } else {
        slide.classList.add(
          'hidden-group'
        );

        slide.setAttribute(
          'data-hidden-group',
          String(
            Math.floor(index / 6)
          )
        );

        slide.classList.add(
          'colour-gallery-hidden-slot'
        );
      }

      if (
        (position - 3) % 6 === 0
      ) {
        slide.classList.add(
          'colour-gallery-square-slot'
        );
      }
    });
  }

  function firstSlide(root) {
    const gallery =
      mainGallery(root);

    if (!gallery) {
      return;
    }

    const control =
      gallery.querySelector(
        '[data-glide-dir="=0"]'
      ) ||
      gallery.querySelector(
        '.glide__bullet'
      );

    if (control) {
      control.click();
    }
  }

  async function updateGallery(
    root,
    reset,
    force
  ) {
    const field =
      colourField(root);

    if (
      !field ||
      !field.select.value
    ) {
      return;
    }

    const selected =
      normalise(
        field.select.value
      );

    const main =
      slidesContainer(
        mainGallery(root)
      );

    const thumbs =
      slidesContainer(
        thumbGallery(root)
      );

    if (!main) {
      return;
    }

    const previous =
      root.getAttribute(
        'data-colour-gallery-applied-colour'
      );

    const changed =
      previous !== selected;

    const shouldSwap =
      changed ||
      Boolean(force);

    const token =
      ++galleryUpdateToken;

    if (shouldSwap) {
      const results =
        await Promise.all([
          swapGallery(
            main,
            selected,
            token
          ),
          swapGallery(
            thumbs,
            selected,
            token
          )
        ]);

      if (
        token !== galleryUpdateToken ||
        !results[0]
      ) {
        return;
      }

      root.setAttribute(
        'data-colour-gallery-applied-colour',
        selected
      );
    }

    if (
      window.matchMedia &&
      window
        .matchMedia(
          DESKTOP_QUERY
        )
        .matches
    ) {
      root.classList.add(
        'product-colour-gallery-active'
      );

      desktopLayout(main);
    } else {
      root.classList.remove(
        'product-colour-gallery-active'
      );

      restoreMobileClasses(main);
    }

    if (
      reset &&
      changed
    ) {
      firstSlide(root);
    }
  }

  function injectStyles() {
    if (
      document.getElementById(
        'product-colour-gallery-css'
      )
    ) {
      return;
    }

    const style =
      document.createElement('style');

    style.id =
      'product-colour-gallery-css';

    style.textContent = `
      .colour-gallery-native-option-hidden {
        display: none !important;
      }

      .colour-gallery-variant-swatches,
      .colour-gallery-size-options {
        display: block;
        margin: 0 0 18px;
        padding: 0;
      }

      .colour-gallery-variant-swatches__title,
      .colour-gallery-size-options__title {
        display: block !important;
        margin: 0 0 8px !important;
        padding: 0 !important;
      }

      .colour-gallery-variant-swatches__list,
      .colour-gallery-size-options__list {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px;
        margin: 0;
        padding: 0;
      }

      .colour-gallery-variant-swatches__button {
        appearance: none;
        -webkit-appearance: none;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        min-height: 38px;
        margin: 0;
        padding: 7px 13px;
        border: 1px solid #d6dee7;
        border-radius: 999px;
        background: #ffffff;
        box-shadow: none;
        color: #002345;
        cursor: pointer;
        font: inherit;
        line-height: 1;
        transition:
          border-color 0.2s ease,
          box-shadow 0.2s ease;
      }

      .colour-gallery-variant-swatches__button:hover {
        border-color: #002345;
      }

      .colour-gallery-variant-swatches__button.is-active {
        border-color: #002345;
        box-shadow:
          0 0 0 1px #002345 inset;
      }

      .colour-gallery-variant-swatches__button:focus-visible,
      .colour-gallery-size-options__button:focus-visible {
        outline: 2px solid #002345;
        outline-offset: 2px;
      }

      .colour-gallery-variant-swatches__chip {
        display: block;
        width: 18px;
        height: 18px;
        flex: 0 0 18px;
        border: 1px solid transparent;
        border-radius: 50%;
        box-sizing: border-box;
      }

      .colour-gallery-variant-swatches__text {
        display: block;
        color: #002345;
        font-size: 14px;
        line-height: 1.2;
        white-space: nowrap;
      }

      .colour-gallery-size-options__button {
        appearance: none;
        -webkit-appearance: none;
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 56px;
        height: 40px;
        margin: 0;
        padding: 0 14px;
        overflow: hidden;
        border: 1px solid #cbd1d8;
        border-radius: 4px;
        background: #ffffff;
        box-shadow: none;
        color: #263342;
        cursor: pointer;
        font: inherit;
        font-size: 14px;
        line-height: 1;
        transition:
          border-color 0.2s ease,
          box-shadow 0.2s ease;
      }

      .colour-gallery-size-options__button:hover:not(:disabled) {
        border-color: #002345;
      }

      .colour-gallery-size-options__button.is-active {
        border-color: #111111;
        box-shadow:
          0 1px 4px rgba(0, 0, 0, 0.18);
      }

      .colour-gallery-size-options__button.is-unavailable,
      .colour-gallery-size-options__button:disabled {
        border-color: #cbd1d8;
        background: #fafafa;
        color: #6f7780;
        cursor: not-allowed;
        opacity: 1;
      }

      .colour-gallery-size-options__button.is-unavailable::after,
      .colour-gallery-size-options__button:disabled::after {
        content: '';
        position: absolute;
        left: 50%;
        top: 50%;
        width: 120%;
        height: 1px;
        background: #8d949c;
        transform:
          translate(-50%, -50%)
          rotate(-27deg);
        pointer-events: none;
      }

      @media screen and (min-width: 750px) {
        .product-colour-gallery-active #product-media-slider .image-snippet.hide-mobile {
          display: none !important;
        }

        .product-colour-gallery-active #product-media-slider .image-snippet.hide-desktop {
          display: block !important;
        }

        .product-colour-gallery-active #product-media-slider .glide__slide.colour-gallery-square-slot {
          margin-left: 0 !important;
          grid-column-start: 1 !important;
          justify-self: start !important;
        }

        .product-colour-gallery-active #product-media-slider .glide__slide.colour-gallery-square-slot .image-snippet-sizer {
          padding-bottom: 100% !important;
        }

        .product-colour-gallery-active #product-media-slider .glide__slide.colour-gallery-square-slot .image-snippet {
          position: relative !important;
          overflow: hidden !important;
        }

        .product-colour-gallery-active #product-media-slider .glide__slide.colour-gallery-square-slot .image-snippet picture,
        .product-colour-gallery-active #product-media-slider .glide__slide.colour-gallery-square-slot .image-snippet img {
          position: absolute !important;
          inset: 0 !important;
          width: 100% !important;
          height: 100% !important;
        }

        .product-colour-gallery-active #product-media-slider .glide__slide.colour-gallery-square-slot .image-snippet img {
          object-fit: cover !important;
          object-position: center !important;
        }
      }

      @media screen and (max-width: 749px) {
        .colour-gallery-variant-swatches__button {
          min-height: 36px;
          padding: 7px 11px;
        }

        .colour-gallery-size-options__button {
          min-width: 52px;
          height: 38px;
          padding: 0 12px;
        }
      }
    `;

    document.head.appendChild(style);
  }

  function scheduleRefresh(
    root,
    reset
  ) {
    pendingReset =
      pendingReset ||
      Boolean(reset);

    window.clearTimeout(
      refreshTimer
    );

    refreshTimer =
      window.setTimeout(
        function () {
          const shouldReset =
            pendingReset;

          pendingReset = false;

          buildControls(root);

          updateGallery(
            root,
            shouldReset,
            false
          );
        },
        70
      );
  }

  function init(root) {
    if (!root) {
      return;
    }

    injectStyles();
    buildControls(root);

    if (
      root.dataset
        .productColourGalleryInitialised ===
      'true'
    ) {
      scheduleRefresh(
        root,
        false
      );

      return;
    }

    root.dataset
      .productColourGalleryInitialised =
      'true';

    root.addEventListener(
      'change',
      function (event) {
        const target =
          event.target;

        if (
          !target ||
          !target.matches(
            'select[data-option-select]'
          )
        ) {
          return;
        }

        const field =
          colourField(root);

        scheduleRefresh(
          root,
          Boolean(
            field &&
            target === field.select
          )
        );
      }
    );

    document.addEventListener(
      'variant:change',
      function () {
        scheduleRefresh(
          root,
          false
        );
      }
    );

    document.addEventListener(
      'product:variant-change',
      function () {
        scheduleRefresh(
          root,
          false
        );
      }
    );

    window.addEventListener(
      'resize',
      function () {
        window.clearTimeout(
          resizeTimer
        );

        resizeTimer =
          window.setTimeout(
            function () {
              updateGallery(
                root,
                false,
                false
              );
            },
            150
          );
      }
    );

    window.setTimeout(
      function () {
        buildControls(root);

        updateGallery(
          root,
          false,
          true
        );
      },
      100
    );
  }

  ready(function () {
    init(rootElement());
  });

  document.addEventListener(
    'shopify:section:load',
    function () {
      init(rootElement());
    }
  );
})();